import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { School, Heart, Home, Briefcase, Recycle, TentTree, FlameKindling, Trees } from 'lucide-react';
import CountUp from 'react-countup';
import { useInView } from 'react-intersection-observer';

export default function CommunityPage() {
  const communityPrograms = [
    {
      title: "Education Initiatives",
      description: "Building schools, providing scholarships, and supporting educational programs in sugar-growing communities.",
      icon: <School className="w-6 h-6 text-blue-800" />,
      bgColor: 'bg-blue-100',
      color: 'text-blue-800',
      image: "https://images.unsplash.com/photo-1497486751825-1233686d5d80?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80"
    },
    {
      title: "Healthcare Services",
      description: "Establishing clinics, health centers, and medical outreach programs for workers and their families.",
      icon: <Heart className="w-6 h-6 text-rose-800" />,
      bgColor: 'bg-rose-100',
      color: 'text-rose-800',
      image: "https://images.unsplash.com/photo-1594567743551-ee026bb6f223?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
    },
    {
      title: "Housing Development",
      description: "Providing affordable housing and improving living conditions for sugar industry workers.",
      icon: <Home className="w-6 h-6 text-green-700" />,
      bgColor: 'bg-green-100',
      color: 'text-green-700',
      image: "https://images.unsplash.com/photo-1672626881472-b96d3eb5445e?q=80&w=1118&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
    },
    {
      title: "Skills Training",
      description: "Vocational training programs to enhance employability and support economic diversification.",
      icon: <Briefcase className="w-6 h-6 text-teal-700" />,
      bgColor: 'bg-teal-100',
      color: 'text-teal-700',
      image: "https://images.unsplash.com/photo-1579004464616-88fc8f835509?q=80&w=1074&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
    }
  ];

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });

  return (
    <div className="min-h-screen py-12">
      <div className="max-w-7xl mx-auto sm:px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-gray-900 mb-6">Sugar in the Community</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Sugar industries are integral parts of their communities, supporting social development, 
            infrastructure, and quality of life improvements across ACP and LDC countries.
          </p>
        </div>

        {/* Community Impact Overview */}
        <section className="mb-16  lg:px-8 px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Community Partnership</h2>
              <p className="text-lg text-gray-600 mb-6">
                Sugar companies and cooperatives work hand-in-hand with local communities, 
                recognizing that their success is intrinsically linked to the wellbeing of 
                the people and places where they operate.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                From providing essential services like healthcare and education to supporting 
                infrastructure development and environmental conservation, sugar industries 
                serve as anchors for community development.
              </p>
              <p className="text-lg text-gray-600">
                This partnership approach ensures that the benefits of sugar production extend 
                far beyond direct employment, creating multiplier effects that strengthen entire regions.
              </p>
            </div>
            <div>
              <ImageWithFallback
                src="https://images.unsplash.com/photo-1559027615-cd4628902d4a?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                alt="Community gathering in sugar farming area"
                className="rounded-lg shadow-lg w-full h-80 object-cover"
              />
            </div>
          </div>
        </section>

        {/* Community Programs */}
        <section className="mb-16 lg:px-8 px-4">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Community Programs</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {communityPrograms.map((program, index) => (
                <Card key={index} className="relative hover:shadow-lg transition-shadow">
                   <div className='relative comm-img h-65'>
                      <ImageWithFallback
                      src={program.image}
                      alt={program.title}
                      className="absolute top-0 right-0 rounded-lg w-full h-full object-cover object-top hover:scale-105 transition-transform duration-800 ease-in-out"
                    />
                    </div>
                   <div className={`comm-tag absolute top-5 left-0 flex items-center space-x-3 mb-4 w-[50%] z-10 ${program.bgColor} pl-5 py-2 rounded-r-full text-amber-600`}>
                      <div>
                        {program.icon}
                      </div>
                      <CardTitle className={`font-semibold ${program.color}`}>{program.title}</CardTitle>
                    </div>
                    <CardContent>
                    <CardDescription className="text-base text-slate-700">
                      {program.description}
                    </CardDescription>
                  </CardContent>
                </Card>
            ))}
          </div>
        </section>

        {/* Social Impact Statistics */}
        <section ref={ref} className="mb-16 bg-amber-50 rounded-lg p-8 lg:px-8 px-4">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Social Impact by the Numbers</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-amber-600 mb-2">
                 {inView && (
                      <CountUp end={500} duration={2} decimals={500 % 1 !== 0 ? 1 : 0} suffix={'+'} />
                )}
              </div>
              <div className="text-gray-700">Schools Supported</div>
              <div className="text-sm text-gray-500 mt-1">Across member countries</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-amber-600 mb-2">
                {inView && (
                      <CountUp end={200} duration={2} decimals={200 % 1 !== 0 ? 1 : 0} suffix={'+'} />
                )}
              </div>
              <div className="text-gray-700">Health Facilities</div>
              <div className="text-sm text-gray-500 mt-1">Clinics and hospitals</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-amber-600 mb-2">
                {inView && (
                      <CountUp end={1} duration={2} decimals={1 % 1 !== 0 ? 1 : 0} suffix={'M+'} />
                )}
              </div>
              <div className="text-gray-700">People Benefited</div>
              <div className="text-sm text-gray-500 mt-1">Direct and indirect beneficiaries</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-amber-600 mb-2">$
                {inView && (
                      <CountUp end={100} duration={2} decimals={100 % 1 !== 0 ? 1 : 0} suffix={'M+'} />
                )}
              </div>
              <div className="text-gray-700">Community Investment</div>
              <div className="text-sm text-gray-500 mt-1">Annual social spending</div>
            </div>
          </div>
        </section>

        {/* Case Studies */}
        <section className="mb-16 w-full">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-20">Community Success Stories</h2>
          <div className="space-y-20 flex gap-2 h-[90vh] overflow-hidden">
            <div className="h-full grid lg:grid-cols-1 lg:grid-rows-[60%_40%] gap-5 items-center">
            <div className="h-full">
              <ImageWithFallback
                src="https://images.unsplash.com/photo-1607990281513-2c110a25bd8c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                alt="Modern school building in Mauritius"
                className="rounded-lg shadow-lg object-cover w-full h-full"
              />
            </div>
            <div className="h-full">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Mauritius: Comprehensive Community Development</h3>
              <p className="text-lg text-gray-600">
                Sugar estates in Mauritius have developed comprehensive community programs 
                including schools, hospitals, recreational facilities, and cultural centers 
                that serve not just employees but entire districts.
              </p>
            </div>
          </div>
          <div className='flex-col h-ful'>
              <div className="h-[47%] mb-[3%] grid lg:grid-cols-[40%_60%] lg:grid-cols-2 lg:grid-rows-1 lg:grid-cols-2 gap-3 items-start justify-items-center">
                  <div className="lg:order-1 order-1 grid-col-1 h-full">
                    <ImageWithFallback
                      src="https://images.unsplash.com/photo-1705579610200-d7e0f6000bb1?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                      alt="Technical training facility"
                      className="rounded-lg w-full h-full object-cover"
                    />
                  </div>
                  <div className='order-2 pr-2'>
                    <h3 className="text-2xl font-bold text-gray-900 mb-5">South Africa: Skills Development Programs</h3>
                    <p className="text-md text-gray-600 mb-4">
                      South African sugar companies have established extensive skills development 
                    programs, including technical colleges, apprenticeships, and continuing 
                    education initiatives.
                    </p>
                     <p className="text-gray-600">
                    This approach has helped maintain cultural identity while improving living 
                    standards and creating opportunities for young people to remain in their 
                    traditional communities.
                  </p>
                  </div>
              </div>

              <div className="h-[47%] grid lg:grid-cols-[40%_60%] lg:grid-cols-2 lg:grid-cols-2 gap-3 justify-items-center">
                 <div className="lg:order-1 order-1 grid-col-1 h-full">
                  <ImageWithFallback
                    src="https://images.unsplash.com/photo-1718152220071-dc4396f654fc?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                    alt="Traditional and modern buildings in Fiji"
                    className="rounded-lg w-full h-full object-cover"
                  />
                </div>
                <div className='order-2 pr-2'>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Fiji: Cultural Preservation</h3>
                  <p className="text-md text-gray-600 mb-4">
                    The Fiji Sugar Corporation works closely with indigenous communities to 
                    preserve traditional practices while providing modern healthcare and 
                    education services.
                  </p>
                  <p className="text-gray-600">
                    This approach has helped maintain cultural identity while improving living 
                    standards and creating opportunities for young people to remain in their 
                    traditional communities.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Environmental and Social Responsibility */}
        <section className="mb-16 lg:px-8 px-4">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Environmental & Social Responsibility</h2>
          <div className="grid md:grid-cols-3 gap-8">
           <Card
              className="text-center hover:shadow-lg transition-shadow shadow-lime-600 group border-green-600 border-2 rounded-md group:hover:bg-green-400"
            >
              <CardHeader className='flex justify-center'>
                <CardTitle className='!p-0 flex gap-2 align-center font-semibold text-md'><Recycle className="text-green-600 scale-110"/> Environmental Stewardship</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription>
                  Protecting watersheds, biodiversity conservation, and sustainable farming 
                  practices that benefit entire ecosystems and communities.
                </CardDescription>
              </CardContent>
            </Card>

            <Card
              className="text-center hover:shadow-lg transition-shadow shadow-green-600 group border-lime-600 border-2 rounded-md group:hover:bg-green-400"
            >
              <CardHeader className='flex justify-center'>
                <CardTitle className='!p-0 flex gap-2 align-center font-semibold text-md'>
                  <TentTree className="text-lime-600 scale-110"/> 
                  Social Inclusion
                </CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription>
                  Promoting gender equality, supporting marginalized groups, and ensuring 
                  that development benefits reach all community members.
                </CardDescription>
              </CardContent>
            </Card>

            <Card
              className="text-center hover:shadow-lg transition-shadow shadow-lime-600 group border-emerald-600 border-2 rounded-md group:hover:bg-lime-400"
            >
              <CardHeader className='flex justify-center'>
                <CardTitle className='!p-0 flex gap-2 align-center font-semibold text-md'>
                  <Trees className="text-emerald-600 scale-110"/> 
                  Cultural Respect
                </CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription>
                  Respecting local traditions, supporting cultural activities, and ensuring 
                  that development is culturally appropriate and community-led.
                </CardDescription>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Call to Action */}
        <section className="text-center bg-gray-50 rounded-lg p-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Building Stronger Communities Together</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
            The partnership between sugar industries and communities continues to evolve, 
            always with the goal of creating sustainable, prosperous, and inclusive societies 
            where everyone can thrive.
          </p>
          <p className="text-gray-600">
            Through continued collaboration, innovation, and commitment to social responsibility, 
            sugar industries will remain vital partners in community development across ACP and LDC countries.
          </p>
        </section>
      </div>
    </div>
  );
}